/**
 * Duplicate suppression after the providers, before the resolver.
 *
 * Two different things look alike and are told apart by provider:
 *  - SAME provider, same value, within `sameProviderMs`: a trigger bounce or a scanner sending the code twice.
 *  - DIFFERENT provider, same value, within `crossProviderMs`: one physical scan delivered over two channels
 *    (a device that emits keystrokes AND a broadcast). Only the FIRST delivery per provider belongs to a group,
 *    so an operator scanning the same item again is never swallowed: the second physical scan starts a new group
 *    as soon as a provider that already contributed to the old group fires again outside `sameProviderMs`.
 *
 * Windows are deliberately short: a deliberate second scan of the same label takes the operator well over
 * 150 ms (release and pull the trigger), while channel skew of one scan is tens of milliseconds.
 */

export type DedupeConfig = {
  readonly sameProviderMs: number;
  readonly crossProviderMs: number;
};

export const DEDUPE_DEFAULTS: DedupeConfig = { sameProviderMs: 150, crossProviderMs: 350 };

export type DedupeInput = {
  readonly value: string;
  readonly symbology?: string | undefined;
  readonly providerId: string;
  /** Arrival time on one clock for every provider (the hub's clock), never a provider's own timestamp. */
  readonly at: number;
};

export type DedupeVerdict =
  | { readonly duplicate: false }
  | { readonly duplicate: true; readonly kind: 'SAME_PROVIDER' | 'CROSS_PROVIDER' };

type Group = {
  readonly value: string;
  readonly symbology: string | undefined;
  readonly firstAt: number;
  readonly seen: Map<string, number>;
};

const MAX_GROUPS = 8;

export class ScanDeduper {
  #cfg: DedupeConfig;
  #groups: Group[] = [];
  /** Each provider's own last delivered scan: a bounce is "the same scan again right after", never "A, B, A". */
  #lastByProvider = new Map<string, { value: string; symbology: string | undefined; at: number }>();

  constructor(cfg: Partial<DedupeConfig> = {}) {
    this.#cfg = { ...DEDUPE_DEFAULTS, ...cfg };
  }

  get config(): DedupeConfig {
    return this.#cfg;
  }

  setConfig(cfg: Partial<DedupeConfig>): void {
    this.#cfg = { ...this.#cfg, ...cfg };
  }

  check(input: DedupeInput): DedupeVerdict {
    const horizon = Math.max(this.#cfg.sameProviderMs, this.#cfg.crossProviderMs) * 2;
    this.#groups = this.#groups.filter((g) => input.at - g.firstAt <= horizon && input.at >= g.firstAt - horizon);

    const sameSymbology = (a: string | undefined, b: string | undefined): boolean => a === undefined || b === undefined || a === b;

    const own = this.#lastByProvider.get(input.providerId);
    if (
      own !== undefined &&
      own.value === input.value &&
      sameSymbology(own.symbology, input.symbology) &&
      input.at >= own.at &&
      input.at - own.at <= this.#cfg.sameProviderMs
    ) {
      return { duplicate: true, kind: 'SAME_PROVIDER' };
    }

    for (let i = this.#groups.length - 1; i >= 0; i -= 1) {
      const g = this.#groups[i]!;
      if (g.value !== input.value || !sameSymbology(g.symbology, input.symbology)) continue;
      if (g.seen.has(input.providerId)) continue; // this provider already delivered for that physical scan
      if (input.at >= g.firstAt && input.at - g.firstAt <= this.#cfg.crossProviderMs) {
        g.seen.set(input.providerId, input.at);
        return { duplicate: true, kind: 'CROSS_PROVIDER' };
      }
    }

    this.#groups.push({
      value: input.value,
      symbology: input.symbology,
      firstAt: input.at,
      seen: new Map([[input.providerId, input.at]]),
    });
    if (this.#groups.length > MAX_GROUPS) this.#groups.shift();
    this.#lastByProvider.set(input.providerId, { value: input.value, symbology: input.symbology, at: input.at });
    return { duplicate: false };
  }

  reset(): void {
    this.#groups = [];
    this.#lastByProvider.clear();
  }
}

/** Sliding-window rate limit: a stuck trigger or a hostile broadcast sender cannot flood the flows. */
export class FloodGuard {
  readonly #max: number;
  readonly #windowMs: number;
  #times: number[] = [];

  constructor(maxPerWindow = 20, windowMs = 1000) {
    this.#max = maxPerWindow;
    this.#windowMs = windowMs;
  }

  allow(now: number): boolean {
    this.#times = this.#times.filter((t) => now - t < this.#windowMs);
    if (this.#times.length >= this.#max) return false;
    this.#times.push(now);
    return true;
  }

  reset(): void {
    this.#times = [];
  }
}
